import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAddTenantAccountId, useGlobalContext } from '../../../GlobalContextProvider';
import { useAccountByAccountNumber, useAccountById } from '@app/sdk';
import { AccountIdContext, useAccountIdContext } from '../AccountIdContext';

export default function AccountSearch() {
  const navigate = useNavigate();
  const { accountIds, setAccountIds, tenantId } = useGlobalContext();
  const { setAccountId } = useContext(AccountIdContext);
  const [searchAccountNumber, setSearchAccountNumber] = useState(undefined as any);
  const [searchAccountId, setSearchAccountId] = useState<string>();
  const [selectedAccountId, setSelectedAccountId] = useState(undefined as any);
  const addAccountId = useAddTenantAccountId();
  const { data: accountByNumber, isLoading: searchingByAccount } = useAccountByAccountNumber(searchAccountNumber);
  const { data: accountById, isLoading: searchingById } = useAccountById(searchAccountId);
  const [accountIdBox, setAccountIdBox] = useState<string>();
  const [accountNumberBox, setAccountNumberBox] = useState<string>();
  const { setBillingDate } = useAccountIdContext();

  useEffect(() => {
    setAccountId();
    setBillingDate(null);
  }, [setAccountId, setBillingDate]);

  const gotoAccount = useCallback(
    (accountId: string) => {
      setAccountId(accountId);
      const billingDate = localStorage.getItem(`${accountId}-billing-date`);
      if (billingDate) {
        setBillingDate(new Date(billingDate));
      }
      addAccountId(accountId);
      setTimeout(() => {
        navigate(`/accounts/${accountId}/summary`);
      }, 100);
    },
    [addAccountId, navigate, setAccountId, setBillingDate]
  );

  useEffect(() => {
    if (accountByNumber?.accountId) {
      gotoAccount(accountByNumber?.accountId);
    }
  }, [accountByNumber, gotoAccount]);

  useEffect(() => {
    if (accountById?.accountId) {
      gotoAccount(accountById?.accountId);
    }
  }, [accountById?.accountId, gotoAccount]);

  function clearAccounts() {
    localStorage.removeItem('accountIds');
    setAccountIds({});
  }

  const onClickGotoAccount = useCallback(() => {
    setSearchAccountId(selectedAccountId);
  }, [selectedAccountId]);

  const searching = searchingByAccount || searchingById;

  return (
    <>
      <div className="ui placeholder segment">
        <div className="ui two column very relaxed stackable grid">
          <div className="column">
            <div className="ui form">
              <div className="field">
                <h3>Account Search</h3>
              </div>
              <div className="field">
                <div className="ui input">
                  <input
                    type="text"
                    placeholder="Account Id"
                    onChange={(e) => setAccountIdBox(e.target.value)}
                    value={accountIdBox || ''}
                  />
                  <button
                    className={`ui right attached blue submit button ${searching && 'loading'}`}
                    onClick={() => setSearchAccountId(accountIdBox)}
                    disabled={!accountIdBox}>
                    Search
                  </button>
                </div>
              </div>
              <div className="field">
                <div className="ui input">
                  <input
                    type="text"
                    placeholder="Account Number"
                    onChange={(e) => setAccountNumberBox(e.target.value)}
                    value={accountNumberBox || ''}
                  />
                  <button
                    className={`ui right attached blue submit button ${searching && 'loading'}`}
                    onClick={() => setSearchAccountNumber(accountNumberBox)}
                    disabled={!accountNumberBox}>
                    Search
                  </button>
                </div>
              </div>
              <div className="field">
                <div className={'ui input right labeled'}>
                  {accountIds && accountIds[tenantId]?.length > 0 && (
                    <>
                      <select
                        className="ui selection dropdown"
                        value={selectedAccountId}
                        onChange={(e) => setSelectedAccountId(e.target.value)}>
                        <option value="">Select Account</option>
                        {accountIds[tenantId].map((accountId) => (
                          <option key={accountId} value={accountId}>
                            {accountId}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
                {accountIds && accountIds[tenantId] && (
                  <div className={'ui buttons'} style={{ marginTop: '5px' }}>
                    <button className={`ui red submit button`} onClick={() => clearAccounts()}>
                      Clear Accounts
                    </button>
                    <button
                      className={`ui blue submit button`}
                      style={{ marginLeft: '5px' }}
                      onClick={() => onClickGotoAccount()}
                      disabled={!selectedAccountId}>
                      Go to Account
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="middle aligned column">
            <button
              className="ui big button"
              onClick={(event) => navigate(event.ctrlKey || event.metaKey ? `/accounts/quick-provision` : `/accounts/create`)}>
              Create Account
            </button>
          </div>
        </div>
        <div className="ui vertical divider">Or</div>
      </div>
    </>
  );
}
